import {
  Alert,
  Avatar,
  Box,
  Button,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import React from "react";
import { useUser } from "../contexts/useUser";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchLLMUsageTiers } from "../dataaccess/fetchLLMUsageTiers";
import { cloneLLMUsageTier } from "../dataaccess/cloneLLMUsageTier";
import { activateLLMUsageTier } from "../dataaccess/activateLLMUsageTier";
import { deactivateLLMUsageTier } from "../dataaccess/deactivateLLMUsageTier";
import { LLMUsageTierStatus } from "../API";
import { countCustomersForUsageTier } from "../dataaccess/countCustomersForUsageTier";
import { useNavigate } from "react-router-dom";

const UsageTiers: React.FC = () => {
  const { project } = useUser();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [nameFilter, setNameFilter] = React.useState<string | undefined>(
    undefined
  );

  // Hooks must be declared unconditionally before any early return
  const cloneMut = useMutation({
    mutationFn: async (tierId: string) => {
      const res = await cloneLLMUsageTier(tierId);
      return (res as any)?.data?.createLLMUsageTier?.id as string | undefined;
    },
    onSuccess: (newId) => {
      queryClient.invalidateQueries({ queryKey: ["usage-tiers"] });
      if (newId) navigate(`/usage-tiers/edit/${newId}`);
    },
  });
  const activateMut = useMutation({
    mutationFn: async (tierId: string) => activateLLMUsageTier(tierId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["usage-tiers"] }),
  });
  const deactivateMut = useMutation({
    mutationFn: async (tierId: string) => deactivateLLMUsageTier(tierId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["usage-tiers"] }),
  });

  const { data, isLoading, isError } = useQuery({
    queryKey: ["usage-tiers"],
    queryFn: () => fetchLLMUsageTiers(project?.id ?? ""),
    enabled: project?.id && project?.id.length > 0 ? true : false,
  });

  const usageTiers =
    data?.data?.usageTiersByProjectId?.items ?? [];

  // Filter usage tiers by name
  const filteredUsageTiers = React.useMemo(() => {
    return usageTiers.filter((tier) => {
      if (
        nameFilter &&
        tier?.name
          ?.toLowerCase()
          .indexOf(nameFilter.toLowerCase()) === -1
      ) {
        return false;
      }
      return true;
    });
  }, [usageTiers, nameFilter]);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (isError) {
    return <div>Error...</div>;
  }

  // Quick actions declared above

  const getStatusColor = (status: string) => {
    switch (status) {
      case "ACTIVE":
        return "primary.main";
      case "DRAFT":
        return "warning.main";
      case "INACTIVE":
        return "secondary.main";
      case "ARCHIVED":
        return "grey.500";
      case "DELETED":
        return "error.main";
      default:
        return "secondary.main";
    }
  };

  // Per-tier customer count cached via React Query
  const useTierCustomerCount = (tierId?: string) => {
    return useQuery({
      queryKey: ["usage-tier-customer-count", tierId],
      queryFn: () => countCustomersForUsageTier(tierId!),
      enabled: Boolean(tierId),
      staleTime: 5 * 60 * 1000,
    });
  };

  const TierCountAvatar: React.FC<{ tierId?: string; status: string }> = ({ tierId, status }) => {
    const { data: count, isLoading } = useTierCustomerCount(tierId);
    const label = isLoading || count === undefined ? "…" : String(count);
    return (
      <Avatar
        sx={{
          bgcolor: getStatusColor(status),
        }}
      >
        {label}
      </Avatar>
    );
  };

  return (
    <>
      <Box p={2}>
        <Alert severity="info" sx={{ mb: 2 }}>
          We suggest our companion product, which has much more usage functionality and a one-setting integration.
          To enable that integration, add your UsageTap API key in Project Settings.
          We will continue to support usage tiers here, but treat it as a guard against abuse rather than a full billing solution.
          <Box sx={{ mt: 1.5 }}>
            <Button
              component="a"
              href="https://UsageTap.com"
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              variant="outlined"
              color="info"
            >
              See UsageTap.com
            </Button>
          </Box>
        </Alert>
        <Grid container spacing={2} sx={{ height: "calc(100vh - 100px)" }}>
          {/* Usage Tiers List */}
          <Grid
            item
            xs={12}
            sx={{ display: "flex", flexDirection: "column", height: "100%" }}
          >
            {/* Filter Panel */}
            <Paper
              elevation={3}
              style={{
                borderRadius: 3,
                height: 120,
                border: "1px solid #444",
                marginBottom: 16,
              }}
            >
              <Box sx={{ p: 2, height: "100%" }}>
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <FilterListIcon fontSize="small" />
                  Usage Tier Filters
                </Typography>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={8}>
                    <TextField
                      variant="outlined"
                      onChange={(e) => setNameFilter(e.currentTarget.value)}
                      value={nameFilter}
                      size="small"
                      fullWidth
                      placeholder="Search usage tiers"
                      InputProps={{
                        startAdornment: (
                          <SearchIcon sx={{ color: "text.secondary", mr: 1 }} />
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <Button
                      variant="outlined"
                      size="small"
                      fullWidth
                      onClick={() => {
                        setNameFilter("");
                      }}
                    >
                      Clear
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Paper>

            {/* Usage Tiers List */}
            <Paper
              elevation={3}
              style={{ borderRadius: 3, border: "1px solid #444", flex: 1 }}
            >
              <Box height="100%" sx={{ overflow: "auto" }}>
                <Box
                  sx={{
                    p: 2,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="h6">
                    Usage Tiers ({filteredUsageTiers.length})
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => navigate("new")}
                  >
                    New Usage Tier
                  </Button>
                </Box>

                <List sx={{ p: 0 }}>
                  {filteredUsageTiers.map((tier) => (
                    <ListItem
                      key={tier?.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        cursor: "pointer",
                        "&:hover": {
                          bgcolor: "action.hover",
                        },
                        pr: 1,
                      }}
                    >
                      <ListItemAvatar sx={{ minWidth: 56 }}>
                        <TierCountAvatar tierId={tier?.id} status={tier?.status ?? ""} />
                      </ListItemAvatar>

                      <Box sx={{ flex: 1, minWidth: 0, mr: 1 }}>
                        <Typography
                          variant="body1"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {tier?.name}
                          {tier?.version ? ` · v${tier.version}` : ""}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Status: {tier?.status}, Period: {tier?.replenishmentPeriod || "None"}, Method: {tier?.replenishmentMethod || "None"}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Standard: {tier?.standardTokenReplenishmentAmount || 0} tokens, {tier?.standardCallReplenishmentAmount || 0} calls
                        </Typography>
                      </Box>

                      <Box sx={{ flexShrink: 0, ml: 1, display: "flex", gap: 1 }}>
                        <Button
                          variant="text"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`edit/${tier?.id}`);
                          }}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="text"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (tier?.id) cloneMut.mutate(tier.id);
                          }}
                        >
                          Clone
                        </Button>
                        <Button
                          variant="text"
                          size="small"
                          disabled={
                            tier?.status === LLMUsageTierStatus.ACTIVE ||
                            tier?.status === LLMUsageTierStatus.ARCHIVED ||
                            tier?.status === LLMUsageTierStatus.DELETED
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            if (tier?.id) activateMut.mutate(tier.id);
                          }}
                        >
                          Activate
                        </Button>
                        <Button
                          variant="text"
                          size="small"
                          disabled={tier?.status !== LLMUsageTierStatus.ACTIVE}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (tier?.id) deactivateMut.mutate(tier.id);
                          }}
                        >
                          Deactivate
                        </Button>
                      </Box>
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default UsageTiers;
